import axios from "axios";
import Swal from "sweetalert2";
import { get_token } from "@/router";

/**
 * RESET STATE
 */
export function reset_service_request_state() {
    return ({
        message: '',
        email: '',
        location: '',
        contact_number: '',
        date: '',
        product_id: null
    });
}

function normalize_service_email(value = '') {
    return String(value || '').trim().toLowerCase();
}

function normalize_service_phone_number(value = '') {
    const trimmed_value = String(value || '').trim();
    const has_plus_prefix = trimmed_value.startsWith('+');
    const digits_only = Array.from(trimmed_value)
        .filter((character) => character >= '0' && character <= '9')
        .join('');

    return (has_plus_prefix ? '+' : '') + digits_only;
}

function get_local_storage_value(key = '') {
    return typeof window !== 'undefined' ? localStorage.getItem(key) || '' : '';
}

export function get_service_request_prefill(user_details = {}) {
    return {
        email: normalize_service_email(user_details?.email || get_local_storage_value('email')),
        contact_number: normalize_service_phone_number(
            user_details?.phone_number || get_local_storage_value('phone_number')
        ),
    };
}

export function sanitize_service_request_phone_number(value = '') {
    return normalize_service_phone_number(value);
}

export function normalize_service_request_date(value = '', min_date = '') {
    if (!String(value || '').trim()) return value;
    if (!String(min_date || '').trim()) return value;

    return new Date(value).getTime() < new Date(min_date).getTime() ? min_date : value;
}

function is_valid_service_email(value = '') {
    const normalized_email = normalize_service_email(value);
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized_email);
}

function is_valid_service_phone_number(value = '') {
    const normalized_phone_number = normalize_service_phone_number(value);
    const digits_only = normalized_phone_number.startsWith('+')
        ? normalized_phone_number.slice(1)
        : normalized_phone_number;

    const has_valid_length = digits_only.length >= 7 && digits_only.length <= 15;
    const has_only_digits = Array.from(digits_only).every(
        (character) => character >= '0' && character <= '9'
    );

    return has_valid_length && has_only_digits;
}

function is_valid_service_request_datetime(value = '') {
    if (!String(value || '').trim()) return false;

    const selected_date = new Date(value);
    if (Number.isNaN(selected_date.getTime())) return false;

    return selected_date.getTime() >= Date.now();
}

/**
 * VALIDATE REQUEST
 */
export function validate_service_request(service_request) {
    const errors = {};
    const normalized_email = normalize_service_email(service_request?.email);
    const normalized_phone_number = normalize_service_phone_number(service_request?.contact_number);

    if (!normalized_email) {
        errors.email = "Email is required.";
    } else if (!is_valid_service_email(normalized_email)) {
        errors.email = "Enter a valid email address.";
    }

    if (!normalized_phone_number) {
        errors.contact_number = "Phone number is required.";
    } else if (!is_valid_service_phone_number(normalized_phone_number)) {
        errors.contact_number = "Enter a valid phone number.";
    }

    if (!service_request.date) {
        errors.service_date = "Preferred date is required.";
    }

    if (
        service_request?.service_details?.type_name === "on_site" &&
        !String(service_request.location || "").trim()
    ) {
        errors.location = "Street address is required.";
    }

    if (Object.keys(errors).length) {
        return ({
            valid: false,
            errors,
            message: 'Please fill in all fields.'
        });
    }

    if (!is_valid_service_request_datetime(service_request.date)) {
        errors.service_date = "You cannot request a past date.";
        return ({
            valid: false,
            errors,
            message: errors.service_date
        });
    }

    return ({
        valid: true,
        errors: {}
    });
}

/**
 * FULL SUBMIT FLOW (ALL IN ONE)
 */
export async function submit_service_request_flow({
    service_request,
    product_id,
    on_success,
    on_error,
    set_loading,
    set_errors
}) {
    // reset loading
    set_loading(true);

    try {
        const validation = validate_service_request(service_request);

        if (!validation.valid) {
            if (set_errors) set_errors(validation.errors);
            set_loading(false);
            return;
        }

        if (set_errors) set_errors({});

        let formData = new FormData();
        formData.append('message', service_request.message);
        formData.append('company_base_profile', service_request?.base_profile?.id);
        formData.append('email', normalize_service_email(service_request.email));
        formData.append('contact_number', normalize_service_phone_number(service_request.contact_number));
        formData.append('request_date', service_request.date);
        formData.append('product_id', product_id);
        formData.append('location', service_request.location || '');

        const response = await axios.post(
            `${process.env.VUE_APP_BACKEND_API_URL}/company_management/request_service/`,
            formData,
            {
                headers: {
                    Authorization: `Token ${get_token()}`
                }
            }
        );

        if (response.data.status === "success") {
            Swal.fire({
                icon: 'success',
                title: 'Success',
                text: 'Service request submitted!',
                timer: 2000,
                showConfirmButton: false,
                position: 'bottom-end',
                toast: true
            });

            if (on_success) on_success(response.data);
        } else {
            Swal.fire({
              title: 'Error',
              text: response.data.message || 'Failed',
              icon: 'error',
              position: 'bottom-end',
              showConfirmButton: false,
              timer: 3000,
              toast: true
            });
            if (on_error) on_error(response.data);
        }

    } catch (error) {
        Swal.fire({
          title: 'Error',
          text: error?.response?.data?.message || 'Failed',
          icon: 'error',
          position: 'bottom-end',
          showConfirmButton: false,
          timer: 3000,
          toast: true
        });

        if (on_error) on_error(error);
    } finally {
        set_loading(false);
    }
}

export function go_to_profile(router, profile) {
    if (!profile?.id || !profile?.profile_type) return;

    router.push({
        path: "/profile",
        query: {
            base: profile.id,
            type: profile.profile_type
        }
    });
}

export function view_profile_product(router,product) {
        const slug = `${slugify(product.name)}-${product.id}`;
        router.push({
        name: "ProductView",
        params: {
            slug: slug
        }
    });
}

export function slugify(text) {
    return (text || '')
        .toLowerCase()
        .trim()
        .replace(/[\s\W-]+/g, '-')
        .replace(/^-+|-+$/g, '');
}
export function format_price(price) {
    return Number(price || 0).toLocaleString('en-ZA', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
}
